// components/OrderChangeLog.js
import React, { useState, useEffect } from 'react';
import { TextField, MenuItem } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css'; // Core grid CSS, only need to import once
import 'ag-grid-community/styles/ag-theme-alpine.css'; // Optional theme CSS
import useManageOrder from './useManageOrder';
import { useAuth } from '../../context/AuthContext';

// changedAt is stored in UTC — always render it in Bangkok time (ICT) so the log
// reads correctly no matter where the admin's machine is set.
const DATE_OPTS = { day: '2-digit', month: 'short', year: '2-digit', timeZone: 'Asia/Bangkok' };
const TIME_OPTS = { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Bangkok' };
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB', DATE_OPTS) : '');
const fmtTime = (v) => (v ? new Date(v).toLocaleTimeString('en-GB', TIME_OPTS).replace(/^24:/, '00:') : '');

const OrderChangeLog = ({order}) => {
  const [rowData, setRowData] = useState([]);
  const [fieldFilter, setFieldFilter] = useState(''); // '' = show all fields
  const {getOrderLogs} = useManageOrder();
  const { authState } = useAuth();
  const isSuperuser = authState?.user?.role === 'superuser';

  useEffect(() => {
    const fetchOrderLogs = async () => {
      if(order){
        const result = await getOrderLogs(order.id)
        if(result && result.length > 0){
          // Keep the raw changedAt on each row so the grid can sort chronologically
          // and format to Bangkok time in the column valueFormatters.
          const formattedData = result.map((log, index) => ({
            no: index + 1,
            changedAt: log.changedAt,
            field: log.fieldName,
            old: log.oldValue,
            new: log.newValue,
            user: log.User ? `${log.User.nickname}` : `System`, // 'System' when changedBy is 999
            source: log.source || '',
          }));
          setRowData(formattedData);
        } else {
          setRowData([]); // reset when switching to an order with no logs
        }
      }
    }
    fetchOrderLogs();
  }, [order]);

  // Field dropdown offers only the field names that actually appear in this
  // order's log (pick from existing data), plus an "all" option.
  const fieldOptions = [...new Set(rowData.map((r) => r.field).filter(Boolean))].sort();
  const displayedRows = fieldFilter ? rowData.filter((r) => r.field === fieldFilter) : rowData;

  const columns = [
    { headerName: "No", field: "no", flex: 1 },
    // Date + Time both bind to the raw changedAt so sorting either is chronological
    // (ISO timestamps sort correctly); newest first by default.
    { headerName: "Date", colId: "date", field: "changedAt", flex: 1, sort: 'desc', valueFormatter: (p) => fmtDate(p.value) },
    { headerName: "Time", colId: "time", field: "changedAt", flex: 1, valueFormatter: (p) => fmtTime(p.value) },
    { headerName: "Field", field: "field", flex: 1 },
    { headerName: "Old", field: "old", flex: 1 },
    { headerName: "New", field: "new", flex: 1 },
    { headerName: "User", field: "user", flex: 1 },
    // Source of the change (e.g. admin.updateOrder) — superuser only.
    ...(isSuperuser ? [{ headerName: "Source", field: "source", flex: 1 }] : []),
  ];

  return (
    <div>
      <div className='flex justify-between'>
        <div>
          <h2 className='text-4xl'>ประวัติการแก้ไขรายการ</h2>
          <TextField label="Contact" variant="outlined" value={order?.contact} disabled />
          <TextField sx={{ ml:2}} label="ชื่อ Smile" variant="outlined" value={order?.smileNameInput} disabled />
        </div>
        <div>
          <img className='h-44 w-80 object-contain' src={order?.imageUrl} />
        </div>
      </div>
      <div className='mt-3'>
        <TextField
          select
          size="small"
          label="กรองตาม Field"
          value={fieldFilter}
          onChange={(e) => setFieldFilter(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">ทั้งหมด</MenuItem>
          {fieldOptions.map((f) => (
            <MenuItem key={f} value={f}>{f}</MenuItem>
          ))}
        </TextField>
      </div>
      <div className="ag-theme-alpine mt-3" style={{ height: 500, width: '100%' }}>
        <AgGridReact
          rowData={displayedRows}
          columnDefs={columns}
          domLayout='autoHeight'
          defaultColDef={{ resizable: true, sortable: true }}
        />
      </div>
    </div>
  );
};

export default OrderChangeLog;
